import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import { getFinalState, getReleaseInfo } from "../../recoil/helpers";
import {
  DetailedRequisitionSerializer,
  PatientReleaseStatus,
} from "../../recoil/types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";
import {
  PatientReleaseModalGridWrapper,
  MissingInfoModalTitle,
  ModalButtons,
  PatientReleaseModalWrapper,
  PatientReleaseModalGridRow,
  PatientReleaseModalGrid,
  PatientReleaseModalText,
  PatientReleaseStatusIndicator,
  PatientReleaseReleaseIndicator,
  PatientReleaseModalKey,
  PatientReleaseModalKeyDescription,
  PatientReleaseModalKeyTitle,
  PatientReleaseModalKeyRow,
} from "./Grid.styles";
import { ReleasePatientData } from "./types";
import { useRequisitionSetter } from "../../recoil/useRequisitionSetter";
import {
  RefreshRequisitionRows,
  ReleasePatientReport,
} from "./Services/GridService";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

export const PatientReleaseModal: React.FC<{
  patientReleaseModalOpen?: ReleasePatientData;
  setPatientReleaseModalOpen: React.Dispatch<
    React.SetStateAction<ReleasePatientData | undefined>
  >;
}> = ({ patientReleaseModalOpen, setPatientReleaseModalOpen }) => {
  const { deviceType } = useDeviceHook();
  const [carrierStatus, setCarrierStatus] = useState<
    PatientReleaseStatus | undefined
  >(undefined);
  const [aneuploidyStatus, setAneuploidyStatus] = useState<
    PatientReleaseStatus | undefined
  >(undefined);
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { setRequisitions, searchText } = useRequisitionSetter();

  useEffect(() => {
    if (patientReleaseModalOpen) {
      setCarrierStatus(patientReleaseModalOpen.row.carrierReleaseStatus);
      setAneuploidyStatus(patientReleaseModalOpen.row.aneuploidyReleaseStatus);
    }
  }, [patientReleaseModalOpen]);

  return (
    <Modal
      open={Boolean(patientReleaseModalOpen)}
      onClose={() => setPatientReleaseModalOpen(undefined)}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <PatientReleaseModalWrapper deviceType={deviceType}>
        <MissingInfoModalTitle deviceType={deviceType}>
          {`PATIENT RELEASE FOR: ${patientReleaseModalOpen?.row.name}`}
        </MissingInfoModalTitle>
        <PatientReleaseModalGridWrapper>
          <PatientReleaseModalGrid>
            <PatientReleaseModalGridRow secondaryRow={false}>
              <PatientReleaseModalText heading>
                REPORT TYPE
              </PatientReleaseModalText>
              <PatientReleaseModalText heading>STATUS</PatientReleaseModalText>
            </PatientReleaseModalGridRow>
            {carrierStatus && aneuploidyStatus && patientReleaseModalOpen && (
              <>
                <PatientReleaseModalGridRow secondaryRow>
                  <PatientReleaseModalText
                    heading={false}
                    bold={carrierStatus === "releasable"}
                  >
                    Carrier Report
                  </PatientReleaseModalText>
                  {carrierStatus === "releasable" ? (
                    <PatientReleaseReleaseIndicator
                      modal
                      button
                      onClick={async () => {
                        try {
                          await setCarrierStatus("releasing");
                          const response = await ReleasePatientReport(
                            auth!.token,
                            true,
                            patientReleaseModalOpen.row.carrierReportID,
                            setLoggedOut,
                          );
                          if (response?.ok) {
                            const responseData =
                              (await response.json()) as DetailedRequisitionSerializer;
                            await setCarrierStatus(
                              getFinalState(
                                responseData.unity_report
                                  ?.patient_release_state || "unreleased",
                              ),
                            );
                            await patientReleaseModalOpen.node.setData({
                              ...patientReleaseModalOpen.row,
                              carrierReleaseStatus: getFinalState(
                                responseData.unity_report
                                  ?.patient_release_state || "unreleased",
                              ),
                              aneuploidyReleaseStatus: aneuploidyStatus,
                            });
                            await RefreshRequisitionRows(
                              auth!.token,
                              setRequisitions,
                              setLoggedOut,
                              searchText,
                            );
                          }
                        } catch (e) {
                          //
                        }
                      }}
                    >
                      Release Report
                    </PatientReleaseReleaseIndicator>
                  ) : (
                    <PatientReleaseStatusIndicator>
                      {getReleaseInfo(carrierStatus)}
                    </PatientReleaseStatusIndicator>
                  )}
                </PatientReleaseModalGridRow>
                <PatientReleaseModalGridRow secondaryRow>
                  <PatientReleaseModalText
                    heading={false}
                    bold={aneuploidyStatus === "releasable"}
                  >
                    Aneuploidy Report
                  </PatientReleaseModalText>
                  {aneuploidyStatus === "releasable" ? (
                    <PatientReleaseReleaseIndicator
                      modal
                      button
                      onClick={async () => {
                        try {
                          await setAneuploidyStatus("releasing");
                          const response = await ReleasePatientReport(
                            auth!.token,
                            false,
                            patientReleaseModalOpen.row.aneuploidyReportID,
                            setLoggedOut,
                          );
                          if (response?.ok) {
                            const responseData =
                              (await response.json()) as DetailedRequisitionSerializer;
                            await setAneuploidyStatus(
                              getFinalState(
                                responseData.aneuploidy_report
                                  ?.patient_release_state || "unreleased",
                              ),
                            );
                            await patientReleaseModalOpen.node.setData({
                              ...patientReleaseModalOpen.row,
                              carrierReleaseStatus: carrierStatus,
                              aneuploidyReleaseStatus: getFinalState(
                                responseData.aneuploidy_report
                                  ?.patient_release_state || "unreleased",
                              ),
                            });
                            await RefreshRequisitionRows(
                              auth!.token,
                              setRequisitions,
                              setLoggedOut,
                              searchText,
                            );
                          }
                        } catch (e) {
                          //
                        }
                      }}
                    >
                      Release Report
                    </PatientReleaseReleaseIndicator>
                  ) : (
                    <PatientReleaseStatusIndicator>
                      {getReleaseInfo(aneuploidyStatus)}
                    </PatientReleaseStatusIndicator>
                  )}
                </PatientReleaseModalGridRow>{" "}
              </>
            )}
          </PatientReleaseModalGrid>
        </PatientReleaseModalGridWrapper>
        <PatientReleaseModalKey>
          <PatientReleaseModalKeyRow>
            <PatientReleaseModalKeyTitle>
              Release Report
            </PatientReleaseModalKeyTitle>{" "}
            <PatientReleaseModalKeyDescription>
              By clicking this button, report will be released to patient
              portal. Patient will have to take action to view report.
            </PatientReleaseModalKeyDescription>
          </PatientReleaseModalKeyRow>
          <PatientReleaseModalKeyRow>
            <PatientReleaseModalKeyTitle>
              Notification Queued
            </PatientReleaseModalKeyTitle>{" "}
            <PatientReleaseModalKeyDescription>
              A notification has been scheduled to be sent, but the system is
              pending confirmation if it has succeeded or failed (24h limit).
            </PatientReleaseModalKeyDescription>
          </PatientReleaseModalKeyRow>
          <PatientReleaseModalKeyRow>
            <PatientReleaseModalKeyTitle>
              Patient Notified
            </PatientReleaseModalKeyTitle>{" "}
            <PatientReleaseModalKeyDescription>
              Patient has received an SMS and/or email informing them the report
              is ready.
            </PatientReleaseModalKeyDescription>
          </PatientReleaseModalKeyRow>
          <PatientReleaseModalKeyRow>
            <PatientReleaseModalKeyTitle>
              Viewed by patient
            </PatientReleaseModalKeyTitle>{" "}
            <PatientReleaseModalKeyDescription>
              Patient has viewed the summary report on the portal.
            </PatientReleaseModalKeyDescription>
          </PatientReleaseModalKeyRow>
          <PatientReleaseModalKeyRow>
            <PatientReleaseModalKeyTitle>
              Downloaded by patient
            </PatientReleaseModalKeyTitle>{" "}
            <PatientReleaseModalKeyDescription>
              Patient has downloaded the full PDF report.
            </PatientReleaseModalKeyDescription>
          </PatientReleaseModalKeyRow>
          <PatientReleaseModalKeyRow>
            <PatientReleaseModalKeyTitle>
              Unable To Contact Patient
            </PatientReleaseModalKeyTitle>{" "}
            <PatientReleaseModalKeyDescription>
              Attempts to contact patient about report have been unsuccessful.
            </PatientReleaseModalKeyDescription>
          </PatientReleaseModalKeyRow>
        </PatientReleaseModalKey>
        <ModalButtons>
          <Button
            label="CLOSE"
            onClick={() => setPatientReleaseModalOpen(undefined)}
            size="small"
          />
        </ModalButtons>
      </PatientReleaseModalWrapper>
    </Modal>
  );
};
